import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiDownload, FiGlobe, FiFileText, FiCode } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { exportToFormat } from '../../services/exportService';
import toast from 'react-hot-toast';

const ExportModal = ({ isOpen, onClose, content, formData }) => {
  if (!content) return null;

  const exportOptions = [
    {
      id: 'html',
      name: 'HTML',
      description: 'Web-ready HTML format',
      icon: FiCode,
      color: 'bg-orange-500'
    },
    {
      id: 'markdown',
      name: 'Markdown',
      description: 'Markdown format for documentation',
      icon: FiFileText,
      color: 'bg-blue-500'
    },
    {
      id: 'text',
      name: 'Plain Text',
      description: 'Simple text format',
      icon: FiFileText,
      color: 'bg-gray-500'
    },
    {
      id: 'indeed',
      name: 'Indeed Ready',
      description: 'Formatted for Indeed job board',
      icon: FiGlobe,
      color: 'bg-blue-600'
    }
  ];

  const handleExport = async (format) => {
    try {
      const result = await exportToFormat(content, format, formData);
      
      if (format === 'indeed') {
        // For Indeed, we'll copy to clipboard with instructions
        await navigator.clipboard.writeText(result.content);
        toast.success('Indeed-formatted content copied! Check the instructions below.');
        
        // Show instructions
        const instructions = `
Instructions for posting to Indeed:
1. Go to Indeed.com and sign in to your employer account
2. Click "Post a Job"
3. Paste the copied content into the job description field
4. Fill in the remaining required fields (title, location, salary if not included)
5. Review and publish your job posting
        `;
        
        setTimeout(() => {
          alert(instructions);
        }, 1000);
      } else {
        // For other formats, trigger download
        const blob = new Blob([result.content], { type: result.mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success(`${format.toUpperCase()} file downloaded successfully!`);
      }
      
      onClose();
    } catch (error) {
      toast.error(`Failed to export as ${format.toUpperCase()}`);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Export Options</h3>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="h-5 w-5 text-gray-500" />
              </motion.button>
            </div>

            <div className="p-6 space-y-3">
              {exportOptions.map((option) => (
                <motion.button
                  key={option.id}
                  whileHover={{ scale: 1.02, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleExport(option.id)}
                  className="w-full flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 text-left"
                >
                  <div className={`flex-shrink-0 w-10 h-10 ${option.color} rounded-lg flex items-center justify-center`}>
                    <SafeIcon icon={option.icon} className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900">{option.name}</h4>
                    <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                  </div>
                  <SafeIcon icon={FiDownload} className="h-4 w-4 text-gray-400" />
                </motion.button>
              ))}
            </div>

            <div className="px-6 pb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700">
                  <strong>Tip:</strong> For job boards like Indeed, use the "Indeed Ready" format for best results.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExportModal;